const GA_MEASUREMENT_ID = 'G-S7GQ0FTY69';

const CONSENT_COOKIE_NAME = 'mapapet_cookie_consent_v1';
const CONSENT_COOKIE_MAX_AGE = 180 * 24 * 60 * 60;

const CONSENT_GRANTED = 'analytics';
const CONSENT_NECESSARY_ONLY = 'necessary';

let googleTagRequested = false;
let googleTagConfigured = false;

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureGtag = () => {
  if (!isBrowser()) return;

  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

const getCookieValue = (name) => {
  if (!isBrowser()) return null;

  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));

  if (!cookie) return null;

  return decodeURIComponent(cookie.substring(name.length + 1));
};

const getCookieDomainPart = () => {
  if (!isBrowser()) return '';

  const hostname = window.location.hostname.toLowerCase();

  if (
    hostname === 'mapapet.com' ||
    hostname === 'www.mapapet.com' ||
    hostname.endsWith('.mapapet.com')
  ) {
    return '; Domain=.mapapet.com';
  }

  return '';
};

const getSecurePart = () => {
  if (!isBrowser()) return '';
  return window.location.protocol === 'https:' ? '; Secure' : '';
};

const saveConsentChoice = (choice) => {
  if (!isBrowser()) return;

  document.cookie =
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(choice)}` +
    `; Max-Age=${CONSENT_COOKIE_MAX_AGE}` +
    '; Path=/' +
    '; SameSite=Lax' +
    getSecurePart() +
    getCookieDomainPart();
};

export const getConsentChoice = () => {
  const value = getCookieValue(CONSENT_COOKIE_NAME);

  if (value === CONSENT_GRANTED || value === CONSENT_NECESSARY_ONLY) {
    return value;
  }

  return null;
};

export const hasAnalyticsConsent = () =>
  getConsentChoice() === CONSENT_GRANTED;

const setDefaultConsent = () => {
  ensureGtag();

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
};

const configureGoogleTag = () => {
  ensureGtag();

  if (googleTagConfigured) return;

  window.gtag('js', new Date());

  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  googleTagConfigured = true;
};

const loadGoogleTag = () => {
  if (!isBrowser()) return;
  if (googleTagRequested) {
    configureGoogleTag();
    return;
  }

  googleTagRequested = true;

  const existingScript = document.querySelector(
    `script[data-mapapet-ga-id="${GA_MEASUREMENT_ID}"]`
  );

  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.dataset.mapapetGaId = GA_MEASUREMENT_ID;
    document.head.appendChild(script);
  }

  configureGoogleTag();
};

const updateConsent = (granted) => {
  ensureGtag();

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

const dispatchConsentChange = () => {
  if (!isBrowser()) return;

  window.dispatchEvent(
    new CustomEvent('mapapet-consent-change', {
      detail: {
        analytics: hasAnalyticsConsent(),
      },
    })
  );
};

export const initializeAnalytics = () => {
  if (!isBrowser()) return;

  setDefaultConsent();

  if (hasAnalyticsConsent()) {
    updateConsent(true);
    loadGoogleTag();
  }
};

export const setAnalyticsConsent = (granted) => {
  if (!isBrowser()) return;

  saveConsentChoice(granted ? CONSENT_GRANTED : CONSENT_NECESSARY_ONLY);
  updateConsent(granted);

  if (granted) {
    loadGoogleTag();
  }

  dispatchConsentChange();
};

export const trackPageView = (pagePath) => {
  if (!isBrowser() || !hasAnalyticsConsent()) return;

  loadGoogleTag();

  const safePath =
    typeof pagePath === 'string' && pagePath.trim()
      ? pagePath
      : `${window.location.pathname}${window.location.search}`;

  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: safePath,
  });
};

export const openCookieSettings = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent('mapapet-open-cookie-settings'));
};

export { GA_MEASUREMENT_ID };
